// packages/web/src/components/LearnerSearchInput.tsx — misma lógica (debounce 350 ms, mín. 2 letras, searchLearners).
// Cambios: combobox accesible (flechas, Enter, Escape), iniciales del alumno, alumno elegido como
// tarjeta con botón "Cambiar", aviso de sede sin sincronizar como texto (no solo emoji).
import { useEffect, useId, useRef, useState } from 'react';
import { Search, Loader2, AlertTriangle } from 'lucide-react';
import { api, type LearnerProfileFull } from '../api';
import { Alert, Button, INPUT_CLASS } from './ui';

const MIN_QUERY_LEN = 2;
const DEBOUNCE_MS = 350;

interface LearnerSearchInputProps {
  siteId: string;
  value: LearnerProfileFull | null;
  onChange: (learner: LearnerProfileFull | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

const initials = (s: string | null | undefined) => (s ?? '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

export function LearnerSearchInput(props: LearnerSearchInputProps) {
  const id = useId();
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<LearnerProfileFull[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) { if (!containerRef.current?.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < MIN_QUERY_LEN) { setMatches([]); return; }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true); setError(null);
      try { const r = await api.searchLearners(trimmed, props.siteId); setMatches(r.matches); setOpen(true); setActive(-1); }
      catch (e) { setError(e instanceof Error ? e.message : String(e)); }
      finally { setLoading(false); }
    }, DEBOUNCE_MS);
  }, [q, props.siteId]);

  function selectMatch(m: LearnerProfileFull) { props.onChange(m); setQ(''); setMatches([]); setOpen(false); }
  function clearSelection() { props.onChange(null); setQ(''); }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(matches.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); selectMatch(matches[active]!); }
    else if (e.key === 'Escape') setOpen(false);
  }

  const showEmpty = open && q.trim().length >= MIN_QUERY_LEN && !loading && matches.length === 0;

  return (
    <div ref={containerRef} className="relative">
      {props.label && (
        <label htmlFor={id} className="block text-sm font-medium text-slate-800">
          {props.label}{props.required && <span className="text-red-700 ml-0.5" aria-hidden>*</span>}
        </label>
      )}

      {props.value ? (
        <div className="mt-1.5 rounded-lg border border-marzano-100 bg-marzano-50 px-3.5 py-2.5 flex items-center gap-3">
          <span aria-hidden className="w-9 h-9 shrink-0 rounded-full bg-white text-marzano-700 font-semibold text-[13px] flex items-center justify-center">{initials(props.value.name ?? props.value.email)}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{props.value.name ?? '(sin nombre)'}</div>
            <div className="text-[13px] text-slate-700 truncate">{props.value.email}{props.value.level_label && ` · ${props.value.level_label}`}</div>
            {props.value.local_site_ids.length === 0 && <div className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-900"><AlertTriangle aria-hidden className="w-3.5 h-3.5" />Sin sede local</div>}
          </div>
          <Button variant="secondary" size="sm" onClick={clearSelection} aria-label="Cambiar alumno">Cambiar</Button>
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <Search aria-hidden className="w-4 h-4 text-slate-500 absolute left-3 top-[calc(50%+3px)] -translate-y-1/2 pointer-events-none" />
            <input id={id} type="text" role="combobox" aria-expanded={open && matches.length > 0} aria-controls={`${id}-list`} aria-autocomplete="list"
              aria-activedescendant={active >= 0 ? `${id}-opt-${active}` : undefined} autoComplete="off"
              className={INPUT_CLASS + ' pl-9 pr-9'} placeholder={props.placeholder ?? 'Nombre (mín. 2 letras) o email'}
              value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => matches.length > 0 && setOpen(true)} onKeyDown={onKeyDown} />
            {loading && <Loader2 aria-label="Buscando" className="w-4 h-4 animate-spin text-slate-500 absolute right-3 top-[calc(50%+3px)] -translate-y-1/2" />}
          </div>

          {open && matches.length > 0 && (
            <ul id={`${id}-list`} role="listbox" className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1">
              {matches.map((m, i) => (
                <li key={m._id} id={`${id}-opt-${i}`} role="option" aria-selected={i === active}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => selectMatch(m)} onMouseEnter={() => setActive(i)}
                  className={`cursor-pointer px-3 py-2 flex items-center gap-3 ${i === active ? 'bg-marzano-50' : ''}`}>
                  <span aria-hidden className="w-8 h-8 shrink-0 rounded-full bg-slate-100 text-slate-700 font-semibold text-xs flex items-center justify-center">{initials(m.name ?? m.email)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-slate-900 truncate">{m.name ?? '(sin nombre)'}</span>
                    <span className="block text-[13px] text-slate-600 truncate">{m.email}{m.level_label && ` · ${m.level_label}`}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {showEmpty && (
            <div role="status" className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg px-3.5 py-3 text-sm text-slate-700">
              Ningún alumno de tu sede coincide con “{q.trim()}”.
            </div>
          )}
          {q.trim().length > 0 && q.trim().length < MIN_QUERY_LEN && <p className="text-[13px] text-slate-600 mt-1.5">Escribe al menos {MIN_QUERY_LEN} letras.</p>}
          {error && <Alert kind="error" className="mt-2">{error}</Alert>}
        </div>
      )}
    </div>
  );
}
